import { Element, Text } from 'slate';

import type { ParagraphElement } from '../../../types';
import type { DiffSegment } from '../../layered-diffs';
import { convertTextToDiffSegment } from '../convertTextToDiffSegment';

/**
 * Converts a paragraph's children (text nodes with suggestedEdits) into a DiffSegment[] array.
 *
 * This extracts the existing diff state from the document:
 * - Text with no suggestedEdits → { op: 'equal', text }
 * - Text with insertNode edit → { id, op: 'insert', text }
 * - Text with deleteNode edit → { id, op: 'delete', text }
 *
 * The resulting DiffSegment[] can be passed to layerDiffs to compute new changes on top.
 *
 * Note: This function handles inline elements (textfields, links, etc.) by processing
 * their text children. We don't use a fully recursive solution because the type system
 * guarantees that inline elements always contain AlineText[] directly - there's no
 * arbitrary nesting. The structure is always: Paragraph → (Text | InlineElement → Text[]).
 */
export function convertParagraphToDiffs(paragraph: ParagraphElement): DiffSegment[] {
  return paragraph.children.flatMap((child): DiffSegment[] => {
    // Handle text nodes directly
    if (Text.isText(child)) {
      return [convertTextToDiffSegment(child)];
    }

    // Handle inline elements (textfield, link, etc.) that contain AlineText children.
    // All inline element types in this codebase have `children: AlineText[]` - they
    // cannot nest other inline elements, so we only need one level of traversal.
    if (Element.isElement(child) && Array.isArray(child.children)) {
      return child.children.flatMap((innerChild): DiffSegment[] => {
        if (Text.isText(innerChild)) {
          return [convertTextToDiffSegment(innerChild)];
        }
        return [];
      });
    }

    return [];
  });
}
